import type { InvestorParticipation, InvestorQuestion, Page } from '@fruuit/shared'
import { Callout, Card, EmptyState, PageHeader, Select, SkeletonText, Tabs } from '@fruuit/ui'
import { ArrowRight, MessagesSquare, UsersRound } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useApiQuery } from '../../api/hooks'
import { ClientActionsPanel } from '../../features/client-actions/ClientActionsPanel'
import { keys, useInvestorEngagements } from '../../features/engagements/queries'
import { ParticipationCard } from '../../features/participation/ParticipationCard'
import { QuestionThread } from '../../features/questions/QuestionThread'
import { useTitle } from '../../lib/useTitle'

const TABS = ['participation', 'questions', 'client-actions'] as const
type Tab = (typeof TABS)[number]

/** S8 Requests (investor): participation requests, questions and client actions. */
export function RequestsPage() {
  useTitle('Requests')
  const [params, setParams] = useSearchParams()
  const tab: Tab = (TABS as readonly string[]).includes(params.get('tab') ?? '')
    ? (params.get('tab') as Tab)
    : 'participation'
  const [show, setShow] = useState<'open' | 'all'>('all')

  const requests = useApiQuery<Page<InvestorParticipation>>(
    [...keys.participation, 'investor', show],
    '/participation-requests',
    { query: { limit: 100, ...(show === 'open' ? { open: 'true' } : {}) } },
  )
  const questions = useApiQuery<Page<InvestorQuestion>>(
    [...keys.questions, 'investor'],
    '/questions',
    { query: { limit: 100 } },
  )
  const engagements = useInvestorEngagements({})
  const companies = useMemo(() => {
    const seen = new Map<string, { issuerId: string; name: string; engagementId: string }>()
    for (const e of engagements.data?.items ?? []) {
      if (!seen.has(e.issuer.id)) {
        seen.set(e.issuer.id, { issuerId: e.issuer.id, name: e.issuer.name, engagementId: e.id })
      }
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [engagements.data])
  const openCases = (engagements.data?.items ?? []).filter((e) => e.status !== 'closed')

  return (
    <>
      <PageHeader
        eyebrow="Investor participation"
        title="Take part in the conversation."
        description="Ask to discuss an engagement, join a future meeting or receive an evidence update. Your questions and decisions stay with each case."
      />
      <Callout tone="info" className="mb-6" title="Nothing leaves Fruuit Engage">
        No email, calendar invitation or company contact is sent. An acknowledgment is a planning
        step, not a confirmed place at a meeting.
      </Callout>
      <Tabs
        label="Request sections"
        value={tab}
        onValueChange={(next) =>
          setParams(next === 'participation' ? {} : { tab: next }, { replace: true })
        }
        items={[
          {
            id: 'participation',
            label: 'Participation',
            count: requests.data?.items.length,
            content: (
              <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
                <section aria-labelledby="requests-heading" className="min-w-0">
                  <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                    <h2 id="requests-heading" className="text-lg font-semibold text-ink">
                      Your requests
                    </h2>
                    <Select
                      label="Show"
                      hideLabel
                      className="w-40"
                      options={[
                        { value: 'all', label: 'All requests' },
                        { value: 'open', label: 'Open only' },
                      ]}
                      value={show}
                      onChange={(event) => setShow(event.target.value as 'open' | 'all')}
                    />
                  </div>
                  {requests.isPending ? (
                    <SkeletonText lines={6} />
                  ) : requests.isError ? (
                    <Callout tone="danger" role="alert">
                      {requests.error.message}
                    </Callout>
                  ) : requests.data.items.length === 0 ? (
                    <EmptyState
                      icon={<UsersRound />}
                      title="No participation requests yet."
                      description='Questions about existing evidence still belong in "Ask the manager" inside each engagement.'
                    />
                  ) : (
                    <div className="space-y-4">
                      {requests.data.items.map((r) => (
                        <ParticipationCard
                          key={r.id}
                          variant="investor"
                          request={r}
                          {...(r.engagement
                            ? { casePath: `/app/engagements/${r.engagement.id}` }
                            : {})}
                        />
                      ))}
                    </div>
                  )}
                </section>
                <Card as="aside" variant="tint" title="Choose an engagement" padding="md">
                  <p className="text-sm leading-relaxed text-ink-soft">
                    Open a case to request participation. The case page checks eligibility with
                    Fruuit's clock, so deadlines are always current.
                  </p>
                  {engagements.isPending ? (
                    <SkeletonText lines={3} className="mt-4" />
                  ) : openCases.length === 0 ? (
                    <p className="mt-4 text-sm text-sage">No open engagements are available.</p>
                  ) : (
                    <ul className="mt-4 divide-y divide-line">
                      {openCases.slice(0, 8).map((e) => (
                        <li key={e.id} className="py-2.5">
                          <Link
                            to={`/app/engagements/${e.id}`}
                            className="group flex items-start justify-between gap-2"
                          >
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-semibold text-ink group-hover:text-teal">
                                {e.issuer.name}
                              </span>
                              <span className="block truncate text-xs text-sage">{e.title}</span>
                            </span>
                            <ArrowRight
                              aria-hidden="true"
                              className="mt-1 size-4 shrink-0 text-teal-500"
                            />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </div>
            ),
          },
          {
            id: 'questions',
            label: 'Questions',
            count: questions.data?.items.length,
            content: questions.isPending ? (
              <SkeletonText lines={6} />
            ) : questions.isError ? (
              <Callout tone="danger" role="alert">
                {questions.error.message}
              </Callout>
            ) : questions.data.items.length === 0 ? (
              <EmptyState
                icon={<MessagesSquare />}
                title="No questions yet."
                description='Open an engagement and use "Ask the manager" to start a thread.'
              />
            ) : (
              <div className="grid gap-4 xl:grid-cols-2">
                {questions.data.items.map((q) => (
                  <div key={q.id} className="min-w-0">
                    <QuestionThread variant="investor" question={q} showEngagement />
                    {q.engagement ? (
                      <Link
                        to={`/app/engagements/${q.engagement.id}`}
                        className="mt-2 inline-flex items-center gap-1 text-[13px] font-medium text-teal hover:underline"
                      >
                        Open engagement <ArrowRight aria-hidden="true" className="size-3.5" />
                      </Link>
                    ) : null}
                  </div>
                ))}
              </div>
            ),
          },
          {
            id: 'client-actions',
            label: 'Client actions',
            content: <ClientActionsPanel companies={companies} />,
          },
        ]}
      />
    </>
  )
}
